import api from '@api/apiServices';
import { CloseRounded } from '@mui/icons-material';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    CircularProgress,
} from '@mui/material';
import {
    CheckoutEventNames,
    initializePaddle,
    PaddleEventData,
} from '@paddle/paddle-js';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

const UpdatePaymentMethod = ({ open, onClose }) => {
    const {
        t,
        i18n: { language: locale },
    } = useTranslation();

    const queryClient = useQueryClient();

    const handlePaddleEvents = (event: PaddleEventData) => {
        if (event.name === CheckoutEventNames.CHECKOUT_COMPLETED) {
            setTimeout(() => {
                console.log(
                    'Invalidating settings query to refresh payment method details',
                );
                queryClient.invalidateQueries({
                    queryKey: ['settings'],
                });
            }, 3000);
            onClose();
        }
    };

    const handlePaddleInitialization = transactionId => {
        initializePaddle({
            environment: import.meta.env.VITE_PADDLE_ENVIRONMENT,
            token: import.meta.env.VITE_PADDLE_TOKEN,
            checkout: {
                settings: {
                    displayMode: 'inline',
                    frameTarget: 'update-payment-method-container',
                    frameInitialHeight: 1,
                    frameStyle:
                        'width: 100%; min-width: 286px; background-color: transparent; border: none;',
                    locale,
                    variant: 'one-page',
                },
            },
            eventCallback: handlePaddleEvents,
        }).then(paddleInstance => {
            paddleInstance?.Checkout.open({ transactionId });
        });
    };

    const { isLoading } = useQuery({
        queryKey: ['update-payment-method-transaction'],
        queryFn: () =>
            api.settings.billing['update-payment-method-transaction']
                .get()
                .then(({ transaction_id }) => {
                    handlePaddleInitialization(transaction_id);
                }),
    });

    return (
        <Dialog
            PaperProps={{
                sx: {
                    backgroundColor: 'surfaceContainerLow.main',
                    minHeight: 620,
                },
            }}
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle>{t('Update Payment Method')}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={theme => ({
                    position: 'absolute',
                    right: 16,
                    top: 16,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseRounded />
            </IconButton>
            <DialogContent
                className="update-payment-method-container"
                sx={{ textAlign: 'center', p: 2, pb: 0 }}
            >
                {isLoading && <CircularProgress sx={{ my: 12 }} />}
            </DialogContent>
        </Dialog>
    );
};

export default UpdatePaymentMethod;
